import React from 'react';
import { useNavigate } from 'react-router-dom';

const UniversalFooter = () => {
  const navigate = useNavigate();

  const footerLinks = {
    product: [
      { label: 'Pricing', path: '/pricing' },
      { label: 'Help Center', path: '/help' },
    ],
    company: [
      { label: 'About Us', path: '/about' },
      { label: 'Contact', path: '/contact' },
      { label: 'FAQ', path: '/faq' },
    ],
    legal: [
      { label: 'Privacy Policy', path: '/privacy' },
      { label: 'Terms of Service', path: '/terms' },
      { label: 'Acceptable Use', path: '/acceptable-use' },
      { label: 'AI Usage Policy', path: '/ai-policy' },
      { label: 'Author & Publisher Addendum', path: '/author-publisher-addendum' },
      { label: 'Reader Credits & Billing', path: '/reader-credits-billing' },
    ],
  };

  // Split legal links into two columns
  const legalColumn1 = footerLinks.legal.slice(0, 3);
  const legalColumn2 = footerLinks.legal.slice(3);

  const handleLinkClick = (path) => {
    if (path.startsWith('/#')) {
      // Handle hash links
      navigate('/');
      setTimeout(() => {
        const id = path.split('#')[1];
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else if (path === '#') {
      // Placeholder links - do nothing for now
      return;
    } else {
      navigate(path);
    }
  };

  return (
    <footer className="bg-slate-900 text-slate-300 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Logo and Description Column */}
          <div className="lg:col-span-1">
            <img 
              src="/newaudia-logo.jpg" 
              alt="NewAudia™" 
              className="h-12 w-auto mb-4 rounded-lg"
            />
            <p className="text-sm text-slate-400 leading-relaxed">
              The future of books. Powered by engagement, built for authors and readers.
            </p>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="text-sm font-semibold text-slate-100 uppercase tracking-wider mb-4">
              Product
            </h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link, idx) => (
                <li key={idx}>
                  <button
                    onClick={() => handleLinkClick(link.path)}
                    className="text-sm text-slate-400 hover:text-slate-100 transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-sm font-semibold text-slate-100 uppercase tracking-wider mb-4">
              Company
            </h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link, idx) => (
                <li key={idx}>
                  <button
                    onClick={() => handleLinkClick(link.path)}
                    className="text-sm text-slate-400 hover:text-slate-100 transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links - Split into 2 columns */}
          <div className="lg:col-span-2">
            <h3 className="text-sm font-semibold text-slate-100 uppercase tracking-wider mb-4">
              Legal
            </h3>
            <div className="grid grid-cols-2 gap-x-8">
              <ul className="space-y-3">
                {legalColumn1.map((link, idx) => (
                  <li key={idx}>
                    <button
                      onClick={() => handleLinkClick(link.path)}
                      className="text-sm text-slate-400 hover:text-slate-100 transition-colors text-left"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
              <ul className="space-y-3">
                {legalColumn2.map((link, idx) => (
                  <li key={idx}>
                    <button
                      onClick={() => handleLinkClick(link.path)}
                      className="text-sm text-slate-400 hover:text-slate-100 transition-colors text-left"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Powered by Emergent */}
        <div className="border-t border-slate-800 py-8">
          <div className="flex justify-center">
            <a 
              href="https://app.emergent.sh/register?ref=rdbr654428"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block group"
            >
              <img 
                src="/emergent-logo.png" 
                alt="Emergent.sh" 
                className="h-auto w-[200px] mx-auto mb-2 transition-transform group-hover:scale-105"
              />
              <p className="text-sm font-semibold text-slate-300 group-hover:text-purple-400 transition-colors text-center">
                Powered By Emergent.sh
              </p>
              <p className="text-xs text-slate-400 group-hover:text-purple-300 transition-colors text-center">
                Build Your Tomorrow Today!
              </p>
            </a>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-slate-800 py-6">
          <div className="flex flex-col items-center text-center space-y-2">
            <p className="text-sm text-slate-400">
              Copyright © 2026 NewAudia<sup className="font-bold text-[0.6em]">TM</sup> Systems, LLC
            </p>
            <a 
              href="https://www.newaudia.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              www.NewAudia.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default UniversalFooter;
